'use client';

import type { TFunction } from 'i18next';
import {
  Bookmark,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import type { FilterParams } from '@/lib/search-filter-params';
import type { FilterTier } from '@/lib/search-gating';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LocaleLink } from '@/components/locale-link';

interface SidebarFooterProps {
  onSaveSearch: ((filters: FilterParams) => void) | undefined;
  collectFilters: () => FilterParams;
  savedSearches:
    | { id: number; name: string; filters: FilterParams }[]
    | undefined;
  isCurrentSearchSaved: boolean;
  t: TFunction<'translation', undefined>;
  onLoadSavedSearch: ((id: number) => void) | undefined;
  handleFormSubmit: () => void;
  tier: FilterTier;
  onOffsetChange: ((offset: number) => void) | undefined;
  offset: number;
  totalLimit: number;
  total: number;
}

export function SidebarFooter({
  onSaveSearch,
  collectFilters,
  savedSearches,
  isCurrentSearchSaved,
  t,
  onLoadSavedSearch,
  handleFormSubmit,
  tier,
  onOffsetChange,
  offset,
  totalLimit,
  total,
}: SidebarFooterProps) {
  return (
    <div className="p-4 border-t bg-background/95 supports-[backdrop-filter]:bg-background/90 backdrop-blur-xs flex flex-col gap-3 shrink-0">
      <div className="flex items-center gap-2">
        {onSaveSearch ? (
          <div className="flex flex-1 items-stretch">
            <Button
              variant="outline"
              onClick={() => onSaveSearch(collectFilters())}
              className={`h-11 lg:h-9 flex-1 rounded-lg text-sm gap-1.5 ${
                savedSearches && savedSearches.length > 0
                  ? 'rounded-r-none border-r-0'
                  : ''
              }`}
            >
              {isCurrentSearchSaved ? (
                <>
                  <Check size={15} className="text-emerald-600" />
                  {t('search.sidebar.searchSaved', { defaultValue: 'Saved' })}
                </>
              ) : (
                <>
                  <Bookmark size={15} />
                  {t('search.sidebar.saveSearch', {
                    defaultValue: 'Save search',
                  })}
                </>
              )}
            </Button>
            {savedSearches && savedSearches.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    aria-label={t('savedSearches.openMenu', {
                      defaultValue: 'Open saved searches',
                    })}
                    className="h-11 lg:h-9 rounded-lg rounded-l-none px-2"
                  >
                    <ChevronDown size={15} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel>
                    {t('savedSearches.title', {
                      defaultValue: 'Saved searches',
                    })}
                  </DropdownMenuLabel>
                  {savedSearches.map((s) => (
                    <DropdownMenuItem
                      key={s.id}
                      onClick={() => onLoadSavedSearch?.(s.id)}
                      className="truncate"
                    >
                      {s.name}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <LocaleLink href="/my-lists?tab=searches">
                      {t('savedSearches.manageAll', {
                        defaultValue: 'Manage all',
                      })}
                    </LocaleLink>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ) : (
          <div className="flex-1" />
        )}
        <Button
          onClick={handleFormSubmit}
          className="flex-1 h-11 lg:h-9 rounded-lg bg-black hover:bg-neutral-800 text-white font-medium gap-1.5 text-sm"
        >
          <Search size={16} />
          {t('search.sidebar.searchButton')}
        </Button>
      </div>

      {/* Pagination & per-page — logged-in users only */}
      {tier !== 'public' &&
        onOffsetChange &&
        (() => {
          const currentPage = Math.floor(offset / totalLimit) + 1;
          const totalPages = Math.ceil(Math.min(total, 5000) / totalLimit);

          // Build page numbers: 1 ... (current-1) current (current+1) ... last
          const pages: (number | '...')[] = [];
          if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
          } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (
              let i = Math.max(2, currentPage - 1);
              i <= Math.min(totalPages - 1, currentPage + 1);
              i++
            ) {
              pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
          }

          return (
            // Desktop pagination now lives in the results bottom bar
            // (DataGridPagination); keep this only for the mobile overlay.
            <div className="flex flex-col gap-2 lg:hidden">
              {/* Page numbers */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={currentPage === 1}
                    onClick={() =>
                      onOffsetChange((currentPage - 2) * totalLimit)
                    }
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  {pages.map((p, i) =>
                    p === '...' ? (
                      <span
                        key={`dots-${i}`}
                        className="text-xs text-muted-foreground px-1"
                      >
                        …
                      </span>
                    ) : (
                      <Button
                        key={p}
                        variant={p === currentPage ? 'primary' : 'outline'}
                        size="sm"
                        className="h-8 w-8 p-0 text-xs"
                        onClick={() => onOffsetChange((p - 1) * totalLimit)}
                      >
                        {p}
                      </Button>
                    ),
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={currentPage === totalPages}
                    onClick={() => onOffsetChange(currentPage * totalLimit)}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}
            </div>
          );
        })()}
    </div>
  );
}
